import { useEffect, useMemo, useRef } from 'react'
import { findQuote, parseBlocks, speakerSlot, timecodeId } from '../../lib/transcript'

// The transcript itself, beside the ledger: every speaker block under its timecode, in the speaker's colour, with
// the spans the ledger quotes marked in place. A click on a ledger line lands here; a click on a mark lands there.

export interface TextMark {
  id: string
  quote: string
  timecode: string | null
  /** requirement | ours | theirs — the colour of the mark */
  kind: 'requirement' | 'ours' | 'theirs'
  label: string
}

export interface TextFocus {
  /** a timecode anchor (tc) or a quoted span (q) */
  kind: 'tc' | 'q'
  id: string
  nonce: number
}

export function TranscriptText({ text, speakers, ourSpeakers, marks, focus, onMark }: {
  text: string; speakers: string[]; ourSpeakers: string[]; marks: TextMark[]; focus: TextFocus | null; onMark: (id: string) => void
}) {
  const blocks = useMemo(() => parseBlocks(text), [text])
  const ours = useMemo(() => new Set(ourSpeakers.map((s) => s.toLowerCase())), [ourSpeakers])
  const container = useRef<HTMLDivElement>(null)

  // Each mark is placed once: in the block under its timecode when the words are there, else the first block that has them.
  const placed = useMemo(() => {
    const byBlock = new Map<number, { start: number; end: number; mark: TextMark }[]>()
    const byTime = new Map<string, number[]>()
    blocks.forEach((b) => { if (b.timecode) { const l = byTime.get(b.timecode) ?? []; l.push(b.index); byTime.set(b.timecode, l) } })
    for (const m of marks) {
      if (!m.quote) continue
      const order = [...(m.timecode ? byTime.get(m.timecode) ?? [] : []), ...blocks.map((b) => b.index)]
      for (const i of order) {
        const span = findQuote(blocks[i].text, m.quote)
        if (!span) continue
        const list = byBlock.get(i) ?? []
        if (list.some((x) => x.start < span[1] && span[0] < x.end)) break   // overlaps an earlier mark in this block: keep the first
        list.push({ start: span[0], end: span[1], mark: m })
        byBlock.set(i, list)
        break
      }
    }
    for (const list of byBlock.values()) list.sort((a, b) => a.start - b.start)
    return byBlock
  }, [blocks, marks])

  useEffect(() => {
    if (!focus || !container.current) return
    const id = focus.kind === 'tc' ? timecodeId(focus.id) : `q-${focus.id}`
    const el = container.current.querySelector<HTMLElement>(`#${CSS.escape(id)}`)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    el.classList.add('flash')
    const t = window.setTimeout(() => el.classList.remove('flash'), 1600)
    return () => window.clearTimeout(t)
  }, [focus])

  return (
    <div ref={container} className="tx-scroll">
      {blocks.map((b) => {
        const slot = speakerSlot(speakers, b.speaker)
        const isOurs = !!b.speaker && ours.has(b.speaker.toLowerCase())
        const spans = placed.get(b.index) ?? []
        return (
          <div key={b.index} id={b.timecode ? timecodeId(b.timecode) : undefined} className={`tx-block${isOurs ? ' ours' : ''}`} style={{ ['--speaker' as string]: `var(--speaker-${slot})` }}>
            {(b.timecode || b.speaker) && (
              <div className="tx-head">
                {b.timecode && <span className="tx-time">{b.timecode}</span>}
                {b.speaker && <span className="tx-speaker"><i />{b.speaker}{isOurs && <em>ours</em>}</span>}
              </div>
            )}
            <p className="tx-text">
              {spans.length === 0 ? b.text : segments(b.text, spans).map((s, i) => s.mark
                ? <mark key={i} id={`q-${s.mark.id}`} className={`tx-mark ${s.mark.kind}`} title={s.mark.label} onClick={() => onMark(s.mark!.id)}>{s.text}</mark>
                : <span key={i}>{s.text}</span>)}
            </p>
          </div>
        )
      })}
    </div>
  )
}

function segments(text: string, spans: { start: number; end: number; mark: TextMark }[]): { text: string; mark: TextMark | null }[] {
  const out: { text: string; mark: TextMark | null }[] = []
  let at = 0
  for (const s of spans) {
    if (s.start < at) continue
    if (s.start > at) out.push({ text: text.slice(at, s.start), mark: null })
    out.push({ text: text.slice(s.start, s.end), mark: s.mark })
    at = s.end
  }
  if (at < text.length) out.push({ text: text.slice(at), mark: null })
  return out
}
